import React, { useEffect, useState, useRef, useCallback } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Toolbar } from 'primereact/toolbar';
import { Toast } from 'primereact/toast';
import DialogHeader from '../../components/ui/DialogHeader';
import { TipoCambioService } from '../../services/TipoCambioService';
import { useAuth } from '../../context/AuthContext';

const TipoCambio = () => {
    const { user } = useAuth();
    const toast = useRef(null);
    const [monedas, setMonedas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    
    // Dialog Config
    const [configDialog, setConfigDialog] = useState(false);
    const [monedaSeleccionada, setMonedaSeleccionada] = useState(null);
    const [config, setConfig] = useState({ comision: 0, aumento: 0 });

    const loadData = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const data = await TipoCambioService.getByEmpresa(user.idEmpresa || 1);
            setMonedas(Array.isArray(data) ? data : data?.data || data?.content || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la configuración de monedas.', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const syncApi = async () => {
        setSyncLoading(true);
        try {
            await TipoCambioService.syncTiposCambio(user.idEmpresa || 1);
            toast.current.show({ severity: 'success', summary: 'Sincronizado', detail: 'Tipos de cambio actualizados con API externa.', life: 3000 });
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Fallo al sincronizar tipos de cambio.', life: 3000 });
        } finally {
            setSyncLoading(false);
        }
    };

    const onToggleEstado = async (rowData) => {
        const nuevoEstado = !rowData.activa;
        try {
            await TipoCambioService.toggleEstado(user.idEmpresa || 1, rowData.codigoIso, nuevoEstado);
            toast.current.show({ severity: 'success', summary: 'Modificado', detail: `Moneda ${nuevoEstado ? 'Activada' : 'Desactivada'}`, life: 3000 });
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cambiar el estado.', life: 3000 });
        }
    };

    const editConfig = (rowData) => {
        setMonedaSeleccionada(rowData);
        setConfig({ comision: rowData.comision || 0, aumento: rowData.aumentoAdicional || 0 });
        setConfigDialog(true);
    };

    const saveConfig = async () => {
        try {
            await TipoCambioService.updateConfig(user.idEmpresa || 1, monedaSeleccionada.codigoIso, {
                comision: config.comision,
                aumento: config.aumento
            });
            toast.current.show({ severity: 'success', summary: 'Configuración Guardada', detail: 'Parámetros actualizados', life: 3000 });
            setConfigDialog(false);
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Fallo al guardar configuración', life: 3000 });
        }
    };

    const estadoBody = (rowData) => (
        <InputSwitch checked={rowData.activa} onChange={() => onToggleEstado(rowData)} />
    );

    const accionBody = (rowData) => (
        <Button icon="pi pi-cog" outlined onClick={() => editConfig(rowData)} title="Configurar Margen" />
    );

    return (
        <div className="grid">
            <div className="col-12">
                <PageHeader 
                    title="Tipo de Cambio (Monedas)" 
                    description="Configure las monedas secundarias y sus mrgenes de tipo de cambio." 
                    icon="pi pi-dollar" 
                />
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4 bg-transparent border-none" left={() => (
                        <Button label="Sincronizar Tipos de Cambio (SUNAT/API)" icon={syncLoading ? "pi pi-spin pi-spinner" : "pi pi-sync"} severity="info" onClick={syncApi} disabled={syncLoading} />
                    )} />

                    <DataTable value={monedas} loading={loading} responsiveLayout="scroll" emptyMessage="No hay monedas configuradas.">
                        <Column field="codigoIso" header="Moneda (ISO)" sortable />
                        <Column field="nombre" header="Nombre" sortable />
                        <Column field="tipoCambioVenta" header="TC Venta" sortable />
                        <Column field="tipoCambioCompra" header="TC Compra" sortable />
                        <Column field="comision" header="% Comisión" body={(r) => `${r.comision}%`} />
                        <Column field="aumentoAdicional" header="Aumento (Fijo)" body={(r) => r.aumentoAdicional} />
                        <Column field="activa" header="Habilitada en POS" body={estadoBody} />
                        <Column body={accionBody} style={{ width: '8rem' }} />
                    </DataTable>

                    <Dialog visible={configDialog} style={{ width: '450px' }} 
                        header={<DialogHeader title={`Configurar: ${monedaSeleccionada?.codigoIso || ''}`} subtitle="Ajuste de márgenes" icon="pi pi-money-bill" />} 
                        modal 
                        footer={(
                        <>
                            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setConfigDialog(false)} />
                            <Button label="Guardar" icon="pi pi-check" onClick={saveConfig} />
                        </>
                    )} onHide={() => setConfigDialog(false)}>
                        <p className="mb-4">Configure los márgenes agregados al tipo de cambio oficial para mitigar riesgos de fluctuación.</p>
                        <div className="field">
                            <label htmlFor="comision" className="font-bold">% Comisión</label>
                            <InputNumber id="comision" value={config.comision} onValueChange={(e) => setConfig({...config, comision: e.value})} suffix="%" className="w-full" />
                        </div>
                        <div className="field">
                            <label htmlFor="aumento" className="font-bold">Aumento Fijo (Ej. +0.05)</label>
                            <InputNumber id="aumento" value={config.aumento} onValueChange={(e) => setConfig({...config, aumento: e.value})} mode="decimal" minFractionDigits={2} className="w-full" />
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default TipoCambio;
